import { InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Dropdown,
  Menu,
  Row,
  Table,
  Tooltip,
  message,
} from 'antd';
import { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  deleteDocumentGroup,
  deleteGroup,
  fetchChildDocuments,
  fetchGroupMembers,
} from '../../api/groups';
import { deletePermissionsByPrincipal } from '../../api/users';
import { GroupDetails, UserDetails } from '../../constants/types';
import { defaultUserGroups } from '../../constants/userGroup';
import { useModal } from '../../hooks/useModal';
import theme from '../../styles/theme';
import { CustomError } from '../../utils/customError';
import { getParentOrigin } from '../../utils/getKeycloakConfig';
import { ActionLink, UsersList, UsersListItem, UsersListModal } from './style';

interface GroupListProps {
  data: GroupDetails[];
  loading: boolean;
  isRealmAdmin: boolean;
  groupIdToHighlight: string | null;
  getGroups(): void;
}

interface TableItem {
  index: number;
  key: string;
  name: string;
  description: string;
}

interface TableItemWithUserDetails extends TableItem {
  users: UserDetails[];
}

const GroupList: FC<GroupListProps> = ({
  data,
  loading,
  isRealmAdmin,
  groupIdToHighlight,
  getGroups,
}) => {
  const intl = useIntl();
  const { showDeleteConfirm } = useModal();
  const [selectedGroupUsersList, setSelectedGroupUsersList] =
    useState<TableItemWithUserDetails>({} as TableItemWithUserDetails);
  const [isUsersListModal, setIsUsersListModal] = useState<boolean>(false);

  const renderEditUserGroupItem = (userGroupId: string) => {
    const parentOrigin = getParentOrigin();
    const label = intl.formatMessage({
      id: 'UserGroup.menu.editUserGroup',
    });

    if (parentOrigin && window.top && window.top !== window) {
      return (
        <div
          data-testid="group-edit"
          role="button"
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              window.top?.postMessage(
                {
                  type: 'redirect',
                  url: `/admin/usergroups/edit/${userGroupId}`,
                },
                parentOrigin,
              );
            }
          }}
          onClick={() => {
            window.top?.postMessage(
              {
                type: 'redirect',
                url: `/admin/usergroups/edit/${userGroupId}`,
              },
              parentOrigin,
            );
          }}
        >
          {label}
        </div>
      );
    } else {
      return (
        <Link data-testid="group-edit" to={`/groups/edit/${userGroupId}`}>
          {label}
        </Link>
      );
    }
  };

  const getGroupId = async (name: string) => {
    try {
      const { data } = await fetchChildDocuments(
        '/user-management/groups',
        name?.trim(),
      );

      return data[0]?.id;
    } catch (error: any) {
      message.error(error?.data?.errors[0]);
      return undefined;
    }
  };

  const handleDeleteGroup = useCallback(
    async (id: string, name: string, documentId?: string) => {
      try {
        // Delete permissions by principal first
        const response = await deletePermissionsByPrincipal(name);
        if (!response) {
          message.warn(
            intl.formatMessage(
              {
                id: 'User.deactivated.removeGroup.warning',
              },
              {
                principal: name,
              },
            ),
          );
        }
        // Delete document group if documentId exists
        if (documentId) {
          await deleteDocumentGroup(documentId);
        }

        // Then delete the group
        await deleteGroup(id);

        message.success(
          intl.formatMessage(
            {
              id: 'UserGroup.successfullyDeleted',
            },
            {
              usergroup: name,
            },
          ),
        );

        getGroups();
      } catch (error: any) {
        if (error instanceof CustomError) {
          message.error(error.message);
        } else {
          message.error(error?.data?.errorMessage ?? error?.data?.errors[0]);
        }
      }
    },
    [getGroups, intl],
  );

  const renderGroupAction = (record: TableItem) => (
    <Menu>
      <Menu.Item key="edit-group">
        {renderEditUserGroupItem(record?.key)}
      </Menu.Item>
      {record.name !== 'tenant-admin' && (
        <>
          {!defaultUserGroups.includes(record.name) && (
            <Menu.Item
              key="delete-group"
              data-testid="group-delete"
              onClick={async () => {
                const documentId = await getGroupId(record.name);
                const config = {
                  showWarning: true,
                  showDeleteInputField: true,
                  title: 'UserGroup.delete.confirm.title',
                  deleteItemNameTitle:
                    'UserGroup.delete.confirm.title.groupName',
                  deleteItemNameValue: record.name,
                  description: 'UserGroup.delete.confirm.title.description',
                  onOkAsync: async () => {
                    await handleDeleteGroup(
                      record?.key,
                      record.name,
                      documentId,
                    );
                  },
                };
                showDeleteConfirm(config);
              }}
              disabled={!isRealmAdmin}
            >
              <FormattedMessage id="UserGroup.menu.deleteUserGroup" />
            </Menu.Item>
          )}
        </>
      )}
    </Menu>
  );

  const getTableData = (): TableItem[] => {
    return data.map((group, index) => ({
      index,
      key: group.id!,
      name: group.name,
      description: group.description!,
    }));
  };

  const onViewUser = async (record: TableItem) => {
    const users = await fetchGroupMembers(record.key);
    setSelectedGroupUsersList({ ...record, users });
    setIsUsersListModal(true);
  };

  const columnHeaders = [
    {
      title: intl.formatMessage({
        id: 'UserGroup.colum.userGroups',
      }),
      dataIndex: '',
      key: 'name',
      sorter: (a: TableItem, b: TableItem) => a.name.localeCompare(b.name),
      render: (records: TableItem) => (
        <Row justify="space-between">
          <Col
            style={{
              wordBreak: 'break-word',
            }}
          >
            {records.name}
          </Col>
          <Col>
            <Tooltip
              title="To set permissions, please go to the “Files” page. "
              placement="topLeft"
            >
              {records.key === groupIdToHighlight && (
                <InfoCircleOutlined
                  style={{
                    cursor: 'pointer',
                    color: theme.palette.primary.main,
                  }}
                  data-testid="group-users-info-highlight"
                />
              )}
            </Tooltip>
          </Col>
        </Row>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'UserGroup.colum.description',
      }),
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => {
        return (
          <span
            style={{
              wordBreak: 'break-word',
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: () => {
        return (
          <>
            {intl.formatMessage({ id: 'UserGroup.colum.user' })}
            <Tooltip
              title={<FormattedMessage id="UserGroup.colum.user.tooltip" />}
              placement="topLeft"
              className="ml-8"
            >
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: theme.palette.primary.main }}
              />
            </Tooltip>
          </>
        );
      },
      dataIndex: '',
      key: 'users',
      width: 160,
      render: (records: TableItem) => (
        <ActionLink
          className="link"
          onClick={() => onViewUser(records)}
          data-testid={`group-users-${records.index}`}
        >
          <FormattedMessage id="UserGroup.colum.viewUsers" />
        </ActionLink>
      ),
    },
    {
      title: () => {
        return <>{intl.formatMessage({ id: 'User.colum.action' })}</>;
      },
      align: 'center',
      fixed: 'right',
      className: 'table-action-column',
      key: 'action',
      width: 95,
      render: (record: TableItem) => {
        return (
          <>
            <Dropdown
              overlay={renderGroupAction(record)}
              trigger={['click']}
              placement="bottomRight"
            >
              <MoreOutlined
                className="three-dot-icon"
                data-testid={`group-options-${record.index}`}
              />
            </Dropdown>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Table
        columns={columnHeaders as any}
        showSorterTooltip={false}
        dataSource={getTableData()}
        loading={loading}
        tableLayout="fixed"
        bordered={false}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          size: 'default',
        }}
        size="middle"
        className="last-2-column-merge"
      />
      <UsersListModal visible={isUsersListModal} closable={false} footer={null}>
        <h3>
          <FormattedMessage id="User.modal.userList" />
        </h3>

        <p>
          <b style={{ marginRight: '10px' }}>
            <FormattedMessage id="ApiKeys.collapse.label.userGroups" />
          </b>
          {selectedGroupUsersList.name}
        </p>

        <UsersList bordered>
          {selectedGroupUsersList.users?.length > 0 &&
            selectedGroupUsersList.users?.map(
              (user: UserDetails, index: number) => (
                <UsersListItem key={index}>
                  {user.firstName}
                  &nbsp;
                  {user.lastName}
                  {user.email && ` | ${user.email}`}
                </UsersListItem>
              ),
            )}
          {selectedGroupUsersList.users?.length === 0 && (
            <UsersListItem>
              <FormattedMessage id="UserGroup.list.placeholder" />
            </UsersListItem>
          )}
        </UsersList>

        <Col style={{ marginTop: '30px' }} span={10} offset={7}>
          <Button
            data-testid="groups-users-close"
            type="primary"
            shape="round"
            block
            onClick={() => {
              setSelectedGroupUsersList({} as TableItemWithUserDetails);
              setIsUsersListModal(false);
            }}
          >
            <FormattedMessage id="Global.close" />
          </Button>
        </Col>
      </UsersListModal>
    </>
  );
};

export default GroupList;
